/** Local send window for Venturu OS emails, inclusive start, exclusive end. */
export const OS_SEND_WINDOW = { startHour: 8, endHour: 19 } as const;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export function isWithinOsSendWindow(localHour: number): boolean {
  return (
    localHour >= OS_SEND_WINDOW.startHour && localHour < OS_SEND_WINDOW.endHour
  );
}

/** Local hour (0-23) for a timezone; falls back to UTC on bad input. */
export function getLocalHour(now: Date, timeZone: string): number {
  try {
    const hour = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      hourCycle: "h23",
    }).format(now);
    const parsed = Number.parseInt(hour, 10);
    return Number.isFinite(parsed) ? parsed % 24 : now.getUTCHours();
  } catch {
    return now.getUTCHours();
  }
}

/** Local calendar date key (YYYY-MM-DD) for a timezone. */
export function getLocalDateKey(now: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** 0 = Sunday … 6 = Saturday in the recipient's timezone. */
export function getLocalWeekday(now: Date, timeZone: string): number {
  try {
    const weekday = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
    }).format(now);
    const index = WEEKDAYS.indexOf(weekday as (typeof WEEKDAYS)[number]);
    return index >= 0 ? index : now.getUTCDay();
  } catch {
    return now.getUTCDay();
  }
}

/** ISO week key (YYYY-Www) for the local calendar date. */
export function getLocalIsoWeekKey(now: Date, timeZone: string): string {
  const key = getLocalDateKey(now, timeZone);
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(
    ((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7,
  );
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/**
 * The quiet-work summary goes out on Monday, at or after the chosen hour,
 * inside the send window, and at most once that ISO week.
 */
export function shouldSendOsDigestNow(input: {
  now: Date;
  timeZone: string;
  digestHour: number;
  lastDigestAt: Date | null;
}): boolean {
  if (getLocalWeekday(input.now, input.timeZone) !== 1) return false;
  const localHour = getLocalHour(input.now, input.timeZone);
  if (localHour < input.digestHour) return false;
  if (!isWithinOsSendWindow(localHour)) return false;
  if (!input.lastDigestAt) return true;
  return (
    getLocalIsoWeekKey(input.lastDigestAt, input.timeZone) !==
    getLocalIsoWeekKey(input.now, input.timeZone)
  );
}

/**
 * Buy box match emails: at most one per local day, at or after the
 * recipient's digest hour, inside the OS send window.
 */
export function shouldSendBuyBoxMatchEmailNow(input: {
  now: Date;
  timeZone: string;
  sendHour: number;
  lastSentAt: Date | null;
}): boolean {
  const localHour = getLocalHour(input.now, input.timeZone);
  if (localHour < input.sendHour) return false;
  if (!isWithinOsSendWindow(localHour)) return false;
  if (!input.lastSentAt) return true;
  return (
    getLocalDateKey(input.lastSentAt, input.timeZone) !==
    getLocalDateKey(input.now, input.timeZone)
  );
}
